import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Trophy,
  Target,
  Clock,
  TrendingUp,
  ArrowRight,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  api,
  OnboardingAssessmentResult,
  OnboardingSkillScore,
} from "@/lib/api";
import { SKILL_DEFINITIONS } from "@/data/assessmentQuestions";
import { getAssessmentRecommendation } from "@/lib/senseiMessages";

const LEVEL_COLORS = {
  novice:
    "text-red-600 bg-red-100 border-red-300 dark:text-red-400 dark:bg-red-900/30 dark:border-red-500",
  developing:
    "text-amber-600 bg-amber-100 border-amber-300 dark:text-amber-400 dark:bg-amber-900/30 dark:border-amber-500",
  proficient:
    "text-blue-600 bg-blue-100 border-blue-300 dark:text-blue-400 dark:bg-blue-900/30 dark:border-blue-500",
  advanced:
    "text-green-600 bg-green-100 border-green-300 dark:text-green-400 dark:bg-green-900/30 dark:border-green-500",
};

const LEVEL_LABELS = {
  novice: "Novice",
  developing: "Developing",
  proficient: "Proficient",
  advanced: "Advanced",
};

export default function AssessmentResults() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get("id");

  const [result, setResult] = useState<OnboardingAssessmentResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadResults = async () => {
      if (!assessmentId) {
        setError("Assessment ID not found");
        setIsLoading(false);
        return;
      }

      try {
        // First evaluate the assessment to get results
        const { result } = await api.evaluateAssessment(assessmentId);
        setResult(result);
      } catch (err: any) {
        // If already evaluated, try to get the assessment directly
        try {
          const assessment = await api.getAssessmentResult(assessmentId);
          if (assessment.result) {
            setResult(assessment.result);
          } else {
            setError("Assessment results not found");
          }
        } catch (innerErr: any) {
          setError(innerErr.message || "Failed to load results");
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadResults();
  }, [assessmentId]);

  const handleViewRoadmap = async () => {
    if (!assessmentId) return;

    setIsGenerating(true);
    try {
      // Complete onboarding first
      await api.completeOnboarding();

      // Generate personalized roadmap
      await api.generatePersonalizedRoadmap(assessmentId);

      // Navigate to roadmap
      navigate("/my-roadmap");
    } catch (err: any) {
      setError(err.message || "Failed to generate roadmap");
      setIsGenerating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="text-gray-500 dark:text-gray-400">
            Analyzing your results...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-red-500">{error}</p>
          <Button
            onClick={() => navigate("/onboarding/goals")}
            variant="outline"
          >
            Start Over
          </Button>
        </div>
      </div>
    );
  }

  if (!result) return null;

  // Group skills by category
  const skillsByCategory: Record<string, OnboardingSkillScore[]> = {};
  result.skill_scores.forEach((skill) => {
    const category = SKILL_DEFINITIONS[skill.skill_id]?.category || "Other";
    if (!skillsByCategory[category]) {
      skillsByCategory[category] = [];
    }
    skillsByCategory[category].push(skill);
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-4 rounded-full bg-indigo-100 dark:bg-indigo-900/50">
              <Trophy className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Trial Complete!
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Here's how you performed across different skill areas
          </p>
        </div>

        {/* Overall Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 text-indigo-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {result.correct_count}/{result.total_questions}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Correct
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {result.accuracy_percent}%
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Accuracy
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-amber-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {(result.avg_time_ms / 1000).toFixed(1)}s
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Avg Time
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Skills Breakdown */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white">
              Warrior Capabilities
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {Object.entries(skillsByCategory).map(([category, skills]) => (
              <div key={category} className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {category}
                </h3>
                <div className="space-y-3">
                  {skills.map((skill) => (
                    <div key={skill.skill_id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {skill.level === "advanced" ||
                          skill.level === "proficient" ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                          )}
                          <span className="text-gray-700 dark:text-gray-200">
                            {skill.skill_name}
                          </span>
                        </div>
                        <span
                          className={`text-xs px-2 py-1 rounded-full border ${
                            LEVEL_COLORS[skill.level]
                          }`}
                        >
                          {LEVEL_LABELS[skill.level]}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Progress value={skill.score} className="h-2 flex-1" />
                        <span className="text-sm text-gray-500 dark:text-gray-400 w-12 text-right">
                          {skill.correct}/{skill.total}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 border-2 border-blue-300 dark:border-blue-600">
          <CardContent className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-3xl" aria-hidden="true">🧙</span>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Your Path Forward
                </h3>
                <p className="text-gray-700 dark:text-gray-200 mb-4 italic">
                  "{getAssessmentRecommendation({
                    strongSkills: result.skill_scores
                      .filter((s) => s.level === "advanced" || s.level === "proficient")
                      .slice(0, 2)
                      .map((s) => s.skill_name),
                    weakSkills: result.skill_scores
                      .filter((s) => s.level === "novice" || s.level === "developing")
                      .slice(0, 2)
                      .map((s) => s.skill_name),
                    overallScore: result.accuracy_percent
                  })}"
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  We shall forge a personalized training path tailored to your current abilities.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA Button */}
        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={handleViewRoadmap}
            disabled={isGenerating}
            className="w-full sm:w-auto px-8 bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating Your Roadmap...
              </>
            ) : (
              <>
                View My Roadmap
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center gap-2">
          <div className="w-3 h-3 rounded-full bg-indigo-400 dark:bg-indigo-500"></div>
          <div className="w-3 h-3 rounded-full bg-indigo-400 dark:bg-indigo-500"></div>
          <div className="w-3 h-3 rounded-full bg-indigo-400 dark:bg-indigo-500"></div>
        </div>
      </div>
    </div>
  );
}
